import { rm } from "node:fs/promises";
import type { BootstrapBundle } from "../../setup/bootstrap-bundle-types.js";
import { flushPendingChanges, restoreWipSnapshot } from "../git-utils.js";
import { selectRestoreSource } from "./restore-precedence.js";
import { buildSnapshotTar, extractSnapshotTar, type SnapshotTarResult } from "./snapshot-tar.js";
import { downloadSnapshotToTemp, putSnapshotToGcs } from "./snapshot-transfer.js";

export { SNAPSHOT_CAPTURED_AT_HEADER } from "./snapshot-transfer.js";

export interface CaptureContext {
  cwd: string;
  branch: string;
  snapshotUploadUrl?: string;
  refreshToken?: () => Promise<string | undefined>;
  wipMessage?: string;
  /** Force the GCS PUT even when the tar is empty (sizeBytes === 0). Used by the
   *  sleep FINALIZE path so `snapshotAt` is stamped and the reconciler's
   *  teardown confirm lands immediately — an empty workspace is a valid (empty)
   *  snapshot; on wake the restore's manifest check falls back to a clean
   *  checkout. The periodic path leaves this false so a racing empty capture
   *  never clobbers a good snapshot. */
  forceUpload?: boolean;
}

export interface SnapshotArtifact {
  fileCount: number;
  deletionCount: number;
  sizeBytes: number;
  gcsUploaded: boolean;
  wipPushed: boolean;
}

export interface RestoreResult {
  source: "gcs" | "wip" | "clean";
  fileCount?: number;
}

let periodicTimer: ReturnType<typeof setInterval> | null = null;

/** The in-flight periodic capture, shared as a PROMISE so finalizeForSleep can
 *  await it — a boolean guard only prevented overlap, it could not stop a slow
 *  pre-finalize capture from PUTting a stale tar AFTER the finalize PUT. */
let inFlightCapture: Promise<SnapshotArtifact> | null = null;

/** Serializes GCS PUTs process-wide: every upload (periodic tick, session
 *  runner flush, finalize) waits for the previous one, so two captures can
 *  never land on the fixed per-workspace GCS object out of order. */
let inFlightGcsUpload: Promise<GcsUploadResult> | null = null;

export interface GcsUploadResult {
  uploaded: boolean;
  fileCount: number;
  deletionCount: number;
  sizeBytes: number;
}

const EMPTY_GCS_RESULT: GcsUploadResult = {
  uploaded: false,
  fileCount: 0,
  deletionCount: 0,
  sizeBytes: 0,
};

async function doUploadSnapshotToGcs(
  cwd: string,
  uploadUrl: string | undefined,
  opts?: { forceUpload?: boolean },
): Promise<GcsUploadResult> {
  let tarResult: SnapshotTarResult;
  try {
    tarResult = await buildSnapshotTar(cwd);
  } catch {
    // Never PUT anything on a failed build — the GCS path is fixed per
    // workspace, so a bogus upload would clobber the last good snapshot.
    return EMPTY_GCS_RESULT;
  }

  try {
    // Normally skip the PUT for an empty tar (nothing changed) so a periodic
    // capture never clobbers a good snapshot. The finalize path forces it so
    // `snapshotAt` is stamped and teardown confirms without waiting the cap.
    const uploaded =
      (opts?.forceUpload || tarResult.sizeBytes > 0) && uploadUrl
        ? await putSnapshotToGcs(uploadUrl, tarResult)
        : false;
    return {
      uploaded,
      fileCount: tarResult.fileCount,
      deletionCount: tarResult.deletionCount,
      sizeBytes: tarResult.sizeBytes,
    };
  } finally {
    await tarResult.cleanup();
  }
}

/**
 * The GCS sink on its own: assemble the snapshot tar (the single capture
 * path) and PUT it to the capability URL. Exposed so a runner whose git-tier
 * flush is driven elsewhere (SessionRunner's periodic flushAllPendingWork)
 * can add the GCS sink to the SAME trigger instead of running a second,
 * racing snapshotter. Calls are serialized: a later capture always PUTs
 * after any earlier in-flight one has settled.
 */
export async function uploadSnapshotToGcs(
  cwd: string,
  uploadUrl: string | undefined,
  opts?: { forceUpload?: boolean },
): Promise<GcsUploadResult> {
  const previous = inFlightGcsUpload;
  const run = (async () => {
    if (previous) await previous.catch(() => {});
    return doUploadSnapshotToGcs(cwd, uploadUrl, opts);
  })();
  inFlightGcsUpload = run;
  try {
    return await run;
  } finally {
    if (inFlightGcsUpload === run) inFlightGcsUpload = null;
  }
}

export async function captureSnapshot(ctx: CaptureContext): Promise<SnapshotArtifact> {
  // GCS failure is non-fatal; the wip push below still preserves this
  // generation.
  const gcs = await uploadSnapshotToGcs(ctx.cwd, ctx.snapshotUploadUrl, {
    forceUpload: ctx.forceUpload,
  });

  const wipResult = await flushPendingChanges(ctx.cwd, {
    wipMessage: ctx.wipMessage ?? "WIP: WorkPreservation periodic snapshot",
    refreshToken: ctx.refreshToken,
  });

  return {
    fileCount: gcs.fileCount,
    deletionCount: gcs.deletionCount,
    sizeBytes: gcs.sizeBytes,
    gcsUploaded: gcs.uploaded,
    wipPushed: wipResult.committed || wipResult.pushed,
  };
}

export function startPeriodic(intervalMs: number, ctx: CaptureContext): void {
  stop();
  periodicTimer = setInterval(() => {
    if (inFlightCapture) return;
    const run = captureSnapshot(ctx);
    inFlightCapture = run;
    void run
      .catch(() => {})
      .finally(() => {
        if (inFlightCapture === run) inFlightCapture = null;
      });
  }, intervalMs);
}

export function stop(): void {
  if (periodicTimer) {
    clearInterval(periodicTimer);
    periodicTimer = null;
  }
}

export async function finalizeForSleep(ctx: CaptureContext): Promise<SnapshotArtifact> {
  stop();
  // Await any in-flight periodic capture BEFORE capturing: both PUT the same
  // fixed GCS object, and a slow pre-finalize upload landing after the
  // finalize upload would leave a stale tar as the "confirmed" snapshot.
  if (inFlightCapture) await inFlightCapture.catch(() => {});

  // Sidecar state (postgres et al.) is no longer durable across sleep/wake
  // (#2623): the durable PVC hosts ONLY the agent's /workspace, and postgres
  // re-seeds from its baked overlayfs seed on wake. So finalize just captures
  // the workspace — no pg_dump. Only /workspace rides the snapshot.
  return captureSnapshot({
    ...ctx,
    wipMessage: ctx.wipMessage ?? "WIP: WorkPreservation sleep finalize snapshot",
    // Always stamp snapshotAt on sleep — even an empty workspace must confirm
    // the finalize so teardown doesn't wait out the reconciler's cap.
    forceUpload: true,
  });
}

/**
 * Restore precedence at boot: GCS snapshot (tracked+untracked+deletions),
 * else the conveyor-wip ref, else a clean checkout. Accepts the narrow slice
 * of the bundle it reads so v3 pods can call it from env-derived values
 * (CONVEYOR_SNAPSHOT_URL + the task branch) as well as from a full bundle.
 *
 * GCS is only authoritative when the archive's manifest HEAD matches the
 * current clone (checked BEFORE any tree mutation) — a stale or invalid
 * snapshot leaves the tree untouched and falls through to the wip ref, whose
 * own parent-HEAD guard handles staleness.
 */
export async function restoreOnBoot(
  bundle: Pick<BootstrapBundle, "snapshotUrl"> & {
    gitPlan: Pick<BootstrapBundle["gitPlan"], "branch">;
  },
  cwd: string,
): Promise<RestoreResult> {
  let gcsAvailable = false;
  let gcsFileCount: number | undefined;

  if (bundle.snapshotUrl) {
    // Streamed to disk — never hold the whole archive in memory.
    const tmpTar = await downloadSnapshotToTemp(bundle.snapshotUrl);
    if (tmpTar) {
      try {
        const result = await extractSnapshotTar(cwd, tmpTar);
        // Success is EXTRACTION success, not a non-zero deletion count — a
        // valid snapshot with zero files/deletions is still authoritative.
        if (result.status === "extracted") {
          gcsAvailable = true;
          gcsFileCount = result.filesExtracted;
        }
      } catch {
        gcsAvailable = false;
      } finally {
        await rm(tmpTar, { force: true }).catch(() => {});
      }
    }
  }

  const wipRestoreResult = gcsAvailable
    ? "none"
    : await restoreWipSnapshot(cwd, bundle.gitPlan.branch);
  const source = selectRestoreSource({ gcsAvailable, wipRestoreResult });
  return source === "gcs" ? { source, fileCount: gcsFileCount } : { source };
}
